import Svg from '@capsule/svg'
import type { ReactElement } from 'jsx-dom-cjs'
import 'shared/assets/svgs/chevronLeft.svg'
import 'shared/assets/svgs/chevronRight.svg'

export default class CarouselLinks {
	private element: HTMLElement | null
	private carousel: Element | null | undefined
	private prevButton: HTMLElement | null | undefined
	private nextButton: HTMLElement | null | undefined
	private scrollBy: number
	private hasOverflow: boolean
	private isBrowserUpdatingFrame: boolean

	constructor(element: HTMLElement) {
		this.element = element
		this.carousel = element?.querySelector('[data-carouselLinks-target]')
		this.prevButton
		this.nextButton
		this.scrollBy = 200
		this.isBrowserUpdatingFrame = false
		this.hasOverflow = Boolean(
			this.carousel && this.carousel.scrollWidth > this.carousel.clientWidth
		)
		this.toggleScroller = this.toggleScroller.bind(this)
		this.initScroller = this.initScroller.bind(this)
		this.scrollToLeft = this.scrollToLeft.bind(this)
		this.scrollToRight = this.scrollToRight.bind(this)
		this.onScroll = this.onScroll.bind(this)
	}

	init() {
		if (this.hasOverflow) {
			this.createNavigationButtons()
			this.initScroller()
			this.addEvents()
			this.setCarouselAttributes()
		}
	}

	addEvents() {
		this.carousel?.addEventListener('scroll', this.onScroll, {
			passive: true
		})

		this.prevButton?.addEventListener('click', this.scrollToLeft)
		this.nextButton?.addEventListener('click', this.scrollToRight)
	}

	/**
	 * Toggle scroller with frame syncing
	 */
	onScroll() {
		if (!this.isBrowserUpdatingFrame) {
			requestAnimationFrame(this.toggleScroller)
			this.isBrowserUpdatingFrame = true
		}
	}

	/**
	 * Scroll left in tagList items
	 */
	scrollToLeft() {
		this.carousel?.scrollBy({
			left: this.scrollBy * -1,
			behavior: 'smooth'
		})
	}

	/**
	 * Scroll right in tagList items
	 */
	scrollToRight() {
		this.carousel?.scrollBy({
			left: this.scrollBy,
			behavior: 'smooth'
		})
	}

	/**
	 * Check if TagList has overflow
	 */
	initScroller() {
		if (!this.element) return
		this.element.classList.toggle('canScrollRight', !this.isScrollEnd)
		this.element.classList.toggle('canScrollLeft', !this.isScrollStart)
	}

	/**
	 * Togle scroller visibility
	 */
	toggleScroller() {
		this.element?.classList.toggle('canScrollRight', !this.isScrollEnd)
		this.element?.classList.toggle('canScrollLeft', !this.isScrollStart)
		this.isBrowserUpdatingFrame = false
	}

	setCarouselAttributes() {
		this.element?.setAttribute('data-carousel', '')
		this.carousel?.setAttribute('aria-live', 'polite')
		this.carousel?.setAttribute('role', 'tablist')
	}

	/**
	 * Check if user scrolled to the end of TagList
	 */
	get isScrollStart() {
		return this.carousel?.scrollLeft === 0
	}

	/**
	 * Check if user scrolled to the end of TagList
	 */
	get isScrollEnd() {
		if (this.carousel) {
			const { scrollLeft, clientWidth, scrollWidth } = this.carousel

			// Add a margin of error to avoid subpixel rendering, rounding errors, or browser zoom
			return Math.ceil(scrollLeft + clientWidth + 2) >= scrollWidth
		}

		return false
	}

	createNavigationButtons() {
		const prevButton = this.createButton({
			type: 'prev',
			title: 'Slide précédente',
			svg: Svg({ name: 'chevronLeft' })
		}) as Element

		const nextButton = this.createButton({
			type: 'next',
			title: 'Slide suivante',
			svg: Svg({ name: 'chevronRight' })
		}) as Element

		this.element?.insertAdjacentElement('afterbegin', nextButton)
		this.element?.insertAdjacentElement('afterbegin', prevButton)

		this.prevButton = this.element?.querySelector('.carouselLinks-nav.prev') as HTMLElement
		this.nextButton = this.element?.querySelector('.carouselLinks-nav.next') as HTMLElement
	}

	createButton({ type, title, svg }: { type: string; title: string; svg: ReactElement }) {
		const button = (
			<button type="button" title={title} className={`carouselLinks-navButton ${type}`} />
		)
		const zone = <div className={`carouselLinks-nav ${type}`}>{button}</div>
		button.appendChild(svg)

		return zone
	}
}
